import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';

/** Datos con los que se compiló una de las dos aplicaciones. */
export interface BuildInformation {
  /** Etiqueta del release, o `dev-<sha>` fuera del pipeline. */
  version: string;
  commit: string;
  branch: string;
  /** Fecha de compilación en ISO. */
  buildDate: string;
}

/**
 * Versión desplegada de cada aplicación, para soporte: al reportar un problema
 * lo primero que hace falta es saber qué build está corriendo el usuario.
 *
 * La del frontend viene del `environment` que genera el pipeline
 * (`scripts/generate-version.cjs`); la del backend se consulta a `GET /version`,
 * que es público y devuelve lo mismo de su lado. Se pide una sola vez y queda
 * en memoria: no cambia mientras la pestaña siga abierta.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly http = inject(HttpClient);

  /** Build del frontend, incrustado en el bundle al compilar. */
  readonly frontend: BuildInformation = environment.buildInformation;

  private readonly backendInterno = signal<BuildInformation | null>(null);
  readonly backend = this.backendInterno.asReadonly();

  /** Versión corta para mostrar de un vistazo (pie del menú). */
  readonly etiqueta = computed(() => this.frontend.version);

  private pedido = false;

  /** Consulta la versión del backend la primera vez que hace falta. */
  cargarBackend(): void {
    if (this.pedido) {
      return;
    }
    this.pedido = true;
    this.http
      .get<BuildInformation>(`${environment.apiUrl}/version`)
      .subscribe({
        next: (info) => this.backendInterno.set(info),
        // Sin versión del backend la pantalla sigue sirviendo: se deja en nulo.
        error: () => this.backendInterno.set(null),
      });
  }

  /** Commit corto (7 caracteres), como se muestra en Git. */
  commitCorto(commit: string | undefined): string {
    if (!commit || commit === 'local') {
      return commit ?? '—';
    }
    return commit.slice(0, 7);
  }

  /** Fecha de compilación en formato local; el ISO crudo no se lee bien. */
  fechaLegible(iso: string | undefined): string {
    if (!iso) {
      return '—';
    }
    const fecha = new Date(iso);
    return Number.isNaN(fecha.getTime()) ? iso : fecha.toLocaleString('es-HN');
  }
}
